import { FilePdfOutlined } from '@ant-design/icons';
import {
  billsCount,
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  dirhams,
  fullDate,
  IdTag,
  isNegative,
  MEMBER_STATUS_LABELS,
  METER_STATUS_LABELS,
  type MemberStatus,
  type MeterStatus,
  type MonthKey,
  monthLabel,
  present,
  WHATSAPP_STATUS_COLORS,
  WHATSAPP_STATUS_LABELS,
} from '@horof/shared';
import { Alert, App, Button, Skeleton, Table, Tabs, Tag } from 'antd';
import dayjs from 'dayjs';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useBlocker, useParams, useSearchParams } from 'react-router';
import { useQuery } from 'urql';
import { BillsList, PaymentsList } from './BillingScreens';
import { MemberDocument, MeterDocument, peopleContext } from './people-data';
import { MemberOverview, MeterOverview } from './RecordOverviews';
import {
  type MemberHistory,
  MemberHistoryDocument,
  MeterHistoryDocument,
} from './record-pages-data';
import { MEMBER_TONE, METER_TONE, PageHeader, Section, Status } from './ui';

const month = (d: string) => monthLabel(d.slice(0, 7) as MonthKey);
const when = (at: string) => `${fullDate(at)} · ${dayjs(at).format('HH:mm')}`;

const CREDIT_LABELS: Record<string, string> = {
  GRANTED: 'رصيد مُنح',
  USED: 'خُصم من فاتورة',
  RELEASED: 'أُرجع بعد تصحيح فاتورة',
  RETURNED: 'أُرجع من فاتورة محذوفة',
};

const NOTICE_LABELS: Record<string, string> = {
  REMINDER: 'تذكير',
  LATE_NOTICE: 'إشعار بالتأخر',
};

/**
 * A record's page (doc 58): its tabs in the address (`?tab=`), and its
 * unsaved edits guarded when changing tab or leaving the page.
 */
function useRecordTabs(first: string) {
  const { modal } = App.useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? first;
  const dirty = useRef(false);
  const onDirtyChange = useCallback((d: boolean) => {
    dirty.current = d;
  }, []);

  const confirmDrop = useCallback(
    () =>
      modal.confirm({
        title: 'تعديلات غير محفوظة',
        content: 'في «البيانات» تعديلات لم تُحفظ. هل تريد تجاهلها؟',
        okText: 'تجاهلها',
        okButtonProps: { danger: true },
        cancelText: 'رجوع',
      }),
    [modal],
  );

  // A tab's own filters and pages don't follow to another tab.
  const setTab = async (next: string) => {
    if (dirty.current && !(await confirmDrop())) return;
    dirty.current = false;
    setParams(next === first ? {} : { tab: next }, { replace: true });
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty.current && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    (async () => {
      if (await confirmDrop()) {
        dirty.current = false;
        blocker.proceed();
      } else blocker.reset();
    })();
  }, [blocker, confirmDrop]);

  return { tab, setTab, onDirtyChange };
}

// ——— Member ———

/**
 * A member's page: the summary, then البيانات (the panel's form, shart
 * people, documents, meters, membership) · الفواتير · الأداءات · الرسائل ·
 * السجل.
 */
export function MemberPage() {
  const { memberId = '' } = useParams();
  const { tab, setTab, onDirtyChange } = useRecordTabs('data');
  const [{ data, error }] = useQuery({
    query: MemberDocument,
    variables: { id: memberId },
  });
  const member = data?.member;

  if (error || (data && !member)) {
    return (
      <section className="desk-page">
        <PageHeader
          breadcrumbs={[{ label: 'الأعضاء', to: '/members' }]}
          title="العضو"
        />
        <Alert
          type={error ? 'error' : 'warning'}
          showIcon
          message={error ? 'تعذّر تحميل العضو' : 'العضو غير موجود'}
        />
      </section>
    );
  }
  if (!member) {
    return (
      <section className="desk-page">
        <Skeleton active paragraph={{ rows: 10 }} />
      </section>
    );
  }

  return (
    <section className="desk-page desk-record">
      <PageHeader
        breadcrumbs={[
          { label: 'الأعضاء', to: '/members' },
          { label: member.fullName ?? 'العضو' },
        ]}
        title={member.fullName}
        status={
          <Status tone={MEMBER_TONE[member.status]}>
            {MEMBER_STATUS_LABELS[member.status as MemberStatus]}
          </Status>
        }
        description={
          <>
            <span>{member.memberAddress}</span>
            {member.committeeRole && (
              <span>{COMMITTEE_ROLE_LABELS[member.committeeRole]}</span>
            )}
            {member.unpaid.totalCount > 0 && (
              <Status tone="warning">
                {billsCount(member.unpaid.totalCount)} غير مؤداة
              </Status>
            )}
          </>
        }
        actions={
          <>
            <IdTag id={member.id} label="" link />
            <Button
              icon={<FilePdfOutlined />}
              href={member.statementLink}
              target="_blank"
            >
              كشف الحساب
            </Button>
          </>
        }
      />
      <Tabs
        activeKey={tab}
        onChange={setTab}
        destroyOnHidden
        items={[
          {
            key: 'data',
            label: 'نظرة عامة',
            children: (
              <MemberOverview member={member} onDirtyChange={onDirtyChange} />
            ),
          },
          {
            key: 'bills',
            label: 'الفواتير',
            children: <BillsList fixed={{ member: member.id }} />,
          },
          {
            key: 'payments',
            label: 'الأداءات',
            children: <PaymentsList fixed={{ member: member.id }} />,
          },
          {
            key: 'messages',
            label: 'الرسائل والتذكيرات',
            children: <MemberMessages id={member.id} />,
          },
          {
            key: 'history',
            label: 'السجل',
            children: <MemberLog id={member.id} />,
          },
        ]}
      />
    </section>
  );
}

function useMemberHistory(id: string) {
  const [{ data, error }] = useQuery({
    query: MemberHistoryDocument,
    variables: { id },
    context: useMemo(() => peopleContext, []),
    requestPolicy: 'cache-and-network',
  });
  return { history: data?.member ?? null, error, loading: !data && !error };
}

function MemberMessages({ id }: { id: string }) {
  const { history, error, loading } = useMemberHistory(id);
  if (error) return <Alert type="error" showIcon message="تعذّر التحميل" />;
  if (loading || !history) return <Skeleton active />;
  const messages = present(history.whatsappMessages.nodes);
  const notices = present(history.notifications.nodes);
  type Message = (typeof messages)[number];
  type Notice = (typeof notices)[number];
  return (
    <div className="desk-record-tables">
      <Section title="رسائل واتساب من الجمعية" counter={messages.length}>
        <Table<Message>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={messages}
          pagination={
            messages.length > 20 ? { pageSize: 20, size: 'small' } : false
          }
          locale={{ emptyText: 'لا رسائل' }}
          columns={[
            {
              key: 'at',
              title: 'الوقت',
              width: 190,
              render: (_, m) => when(m.sentAt ?? m.createdAt),
            },
            {
              key: 'bill',
              title: 'الفاتورة',
              width: 140,
              render: (_, m) =>
                m.bill ? (
                  <Link to={`/bills/${m.bill.id}`}>
                    {month(m.bill.billMonth)}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              key: 'status',
              title: 'الحالة',
              width: 110,
              render: (_, m) => (
                <Tag color={WHATSAPP_STATUS_COLORS[m.status]}>
                  {WHATSAPP_STATUS_LABELS[m.status]}
                </Tag>
              ),
            },
            {
              key: 'to',
              title: 'إلى',
              width: 150,
              render: (_, m) => <span className="ltr-num">{m.toPhone}</span>,
            },
            {
              key: 'note',
              title: 'ملاحظة',
              ellipsis: true,
              render: (_, m) =>
                m.error ? (
                  <span className="field-error">{m.error}</span>
                ) : (
                  <span className="field-hint">{m.queuedBy}</span>
                ),
            },
          ]}
        />
      </Section>
      <Section title="التذكيرات والإشعارات" counter={notices.length}>
        <Table<Notice>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={notices}
          pagination={
            notices.length > 20 ? { pageSize: 20, size: 'small' } : false
          }
          locale={{ emptyText: 'لم يُذكَّر هذا العضو' }}
          columns={[
            {
              key: 'at',
              title: 'الوقت',
              width: 190,
              render: (_, n) => when(n.sentAt),
            },
            {
              key: 'kind',
              title: 'النوع',
              width: 140,
              render: (_, n) => NOTICE_LABELS[n.kind] ?? n.kind,
            },
            {
              key: 'channel',
              title: 'الوسيلة',
              width: 110,
              render: (_, n) => CHANNEL_LABELS[n.channel],
            },
            {
              key: 'due',
              title: 'المبلغ المتأخر',
              width: 140,
              render: (_, n) => dirhams(n.amountDue),
            },
            {
              key: 'by',
              title: 'أرسله',
              ellipsis: true,
              render: (_, n) => (
                <span>
                  {n.sentBy ?? '—'}{' '}
                  <span className="field-hint ltr-num">{n.phone}</span>
                </span>
              ),
            },
          ]}
        />
      </Section>
    </div>
  );
}

function MemberLog({ id }: { id: string }) {
  const { history, error, loading } = useMemberHistory(id);
  if (error) return <Alert type="error" showIcon message="تعذّر التحميل" />;
  if (loading || !history) return <Skeleton active />;
  return (
    <div className="desk-record-tables">
      <StatusLog
        rows={present(history.memberStatusChanges.nodes).map((c) => ({
          id: c.id,
          on: c.changedOn,
          from:
            MEMBER_STATUS_LABELS[c.fromStatus as MemberStatus] ?? c.fromStatus,
          to: MEMBER_STATUS_LABELS[c.toStatus as MemberStatus] ?? c.toStatus,
          reason: c.reason,
          by: c.changedBy,
        }))}
      />
      <CreditLog history={history} />
    </div>
  );
}

function CreditLog({ history }: { history: MemberHistory }) {
  const rows = present(history.memberCredits.nodes);
  type Row = (typeof rows)[number];
  return (
    <Section
      title={`رصيد العضو: ${dirhams(history.creditBalance)}`}
      description="حركات الرصيد كما سجّلها arqam."
    >
      <Table<Row>
        rowKey="id"
        size="small"
        tableLayout="fixed"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: 'لا حركة في الرصيد' }}
        columns={[
          {
            key: 'at',
            title: 'الوقت',
            width: 190,
            render: (_, c) => when(c.createdAt),
          },
          {
            key: 'kind',
            title: 'الحركة',
            width: 190,
            render: (_, c) => CREDIT_LABELS[c.kind] ?? c.kind,
          },
          {
            key: 'amount',
            title: 'المبلغ',
            width: 130,
            render: (_, c) => (
              <span className={isNegative(c.amount) ? 'is-debit' : 'is-credit'}>
                {dirhams(c.amount)}
              </span>
            ),
          },
          {
            key: 'bill',
            title: 'الفاتورة',
            width: 140,
            render: (_, c) =>
              c.bill ? (
                <Link to={`/bills/${c.bill.id}`}>
                  {month(c.bill.billMonth)}
                </Link>
              ) : (
                '—'
              ),
          },
          {
            key: 'reason',
            title: 'السبب',
            ellipsis: true,
            render: (_, c) => (
              <span>
                {c.reason ?? '—'}{' '}
                {c.createdBy && (
                  <span className="field-hint">· {c.createdBy}</span>
                )}
              </span>
            ),
          },
        ]}
      />
    </Section>
  );
}

type StatusRow = {
  id: string;
  on: string;
  from: string;
  to: string;
  reason: string | null;
  by: string | null;
  extra?: ReactNode;
};

function StatusLog({ rows }: { rows: StatusRow[] }) {
  return (
    <Section title="تغيّرات الحالة">
      <Table<StatusRow>
        rowKey="id"
        size="small"
        tableLayout="fixed"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: 'لم تتغيّر الحالة' }}
        columns={[
          {
            key: 'on',
            title: 'التاريخ',
            width: 140,
            render: (_, r) => fullDate(r.on),
          },
          {
            key: 'change',
            title: 'من ← إلى',
            width: 220,
            render: (_, r) => `${r.from} ← ${r.to}`,
          },
          {
            key: 'reason',
            title: 'السبب',
            ellipsis: true,
            render: (_, r) => (
              <span>
                {r.reason ?? '—'} {r.extra}
              </span>
            ),
          },
          {
            key: 'by',
            title: 'بواسطة',
            width: 150,
            ellipsis: true,
            render: (_, r) => r.by ?? '—',
          },
        ]}
      />
    </Section>
  );
}

// ——— Meter ———

/**
 * A meter's page: who and where, then البيانات (the panel's form, photo,
 * status) · القراءات والفواتير · السجل (status changes, replacements).
 */
export function MeterPage() {
  const { meterId = '' } = useParams();
  const { tab, setTab, onDirtyChange } = useRecordTabs('data');
  const [{ data, error }] = useQuery({
    query: MeterDocument,
    variables: { id: meterId },
    context: useMemo(() => peopleContext, []),
  });
  const meter = data?.meter;

  if (error || (data && !meter)) {
    return (
      <section className="desk-page">
        <PageHeader
          breadcrumbs={[{ label: 'العدادات', to: '/meters' }]}
          title="العداد"
        />
        <Alert
          type={error ? 'error' : 'warning'}
          showIcon
          message={error ? 'تعذّر تحميل العداد' : 'العداد غير موجود'}
        />
      </section>
    );
  }
  if (!meter) {
    return (
      <section className="desk-page">
        <Skeleton active paragraph={{ rows: 10 }} />
      </section>
    );
  }

  return (
    <section className="desk-page desk-record">
      <PageHeader
        breadcrumbs={[
          { label: 'العدادات', to: '/meters' },
          { label: meter.label ?? 'العداد' },
        ]}
        title={`العداد ${meter.label}`}
        status={
          <Status tone={METER_TONE[meter.meterStatus]}>
            {METER_STATUS_LABELS[meter.meterStatus as MeterStatus]}
          </Status>
        }
        description={
          <>
            {meter.member && (
              <Link to={`/members/${meter.member.id}`}>
                {meter.member.fullName}
              </Link>
            )}
            <span>{meter.meterAddress}</span>
          </>
        }
        actions={<IdTag id={meter.id} label="" link />}
      />
      <Tabs
        activeKey={tab}
        onChange={setTab}
        destroyOnHidden
        items={[
          {
            key: 'data',
            label: 'نظرة عامة',
            children: (
              <MeterOverview meter={meter} onDirtyChange={onDirtyChange} />
            ),
          },
          {
            key: 'readings',
            label: 'القراءات والفواتير',
            children: <BillsList fixed={{ meter: meter.id }} />,
          },
          {
            key: 'history',
            label: 'السجل',
            children: <MeterLog id={meter.id} />,
          },
        ]}
      />
    </section>
  );
}

function MeterLog({ id }: { id: string }) {
  const [{ data, error }] = useQuery({
    query: MeterHistoryDocument,
    variables: { id },
    context: useMemo(() => peopleContext, []),
    requestPolicy: 'cache-and-network',
  });
  if (error) return <Alert type="error" showIcon message="تعذّر التحميل" />;
  const meter = data?.meter;
  if (!meter) return <Skeleton active />;
  const replaced = present(meter.replaced.nodes);
  return (
    <div className="desk-record-tables">
      <StatusLog
        rows={present(meter.meterStatusChanges.nodes).map((c) => ({
          id: c.id,
          on: c.changedOn,
          from:
            METER_STATUS_LABELS[c.fromStatus as MeterStatus] ?? c.fromStatus,
          to: METER_STATUS_LABELS[c.toStatus as MeterStatus] ?? c.toStatus,
          reason: c.reason,
          by: c.changedBy,
          extra: (
            <>
              {c.reading !== null && (
                <span className="field-hint">
                  · القراءة <span className="ltr-num">{c.reading}</span>
                </span>
              )}
              {c.meterByReplacedBy && (
                <span>
                  {' '}
                  · العداد الجديد:{' '}
                  <Link to={`/meters/${c.meterByReplacedBy.id}`}>
                    {c.meterByReplacedBy.label}
                  </Link>
                </span>
              )}
            </>
          ),
        }))}
      />
      {replaced.length > 0 && (
        <Section title="حلّ هذا العداد محلّ">
          {replaced.map((m) => (
            <Link key={m.id} to={`/meters/${m.id}`} className="desk-line">
              العداد {m.label}
            </Link>
          ))}
        </Section>
      )}
    </div>
  );
}
